import React, { useRef, useEffect } from "react";
import { useFormContext, useController } from "react-hook-form";
import "./Form.css";
import { TextCss } from "../../components/GlobalCss/TextCss";
import {
  hasInputCharFilter,
  sanitizeInputValue,
  SINGLE_ALPHABET_RE,
  SINGLE_DIGIT_RE,
  SPECIAL_CHAR_RE,
} from "./inputFieldSanitize";

interface InputFieldProps {
  name: string;
  label: string;
  required?: boolean;
  isAsterisk?: boolean;
  placeholder?: string;
  type?: React.HTMLInputTypeAttribute;
  requiredMessage?: string;
  value?: string;
  validation?: {
    required?: string | boolean;
    min?: number | { value: number; message: string };
    max?: number | { value: number; message: string };
    pattern?: { value: RegExp; message: string };
    validate?: (value: any) => boolean | string;
    [key: string]: any;
  };
  className?: string;
  disabled?: boolean;
  min?: string | number;
  max?: string | number;
  maxLength?: number;

  // Restriction props
  onlyAlphabets?: boolean;
  onlyNumeric?: boolean;
  allowDecimal?: boolean;
  allowSpecialChars?: boolean; //  Naya prop
  uppercase?: boolean;
}

type RHFValidateFn = (value: unknown) => boolean | string;
type RHFValidate =
  | RHFValidateFn
  | Record<string, RHFValidateFn>;

function mergeRHFValidate(
  ...parts: Array<RHFValidate | undefined>
): Record<string, RHFValidateFn> | undefined {
  const named: Record<string, RHFValidateFn> = {};
  let fnIndex = 0;
  for (const part of parts) {
    if (part == null) continue;
    if (typeof part === "function") {
      named[`custom${fnIndex++}`] = part;
    } else {
      Object.assign(named, part);
    }
  }
  return Object.keys(named).length > 0 ? named : undefined;
}

const InputField: React.FC<InputFieldProps> = ({
  name,
  label,
  value,
  isAsterisk = true,
  required = false,
  placeholder,
  type = "text",
  requiredMessage = "This field is required",
  validation = {},
  className = "",
  disabled = false,

  onlyAlphabets = false,
  onlyNumeric = false,
  allowDecimal = false,
  allowSpecialChars = false, // by default block special chars
  uppercase = false,

  min,
  max,
  ...rest
}) => {
  const {
    control,
    formState: { isSubmitted },
    setError,
    clearErrors,
    trigger,
  } = useFormContext();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isNumericMode = type === "number" || onlyNumeric;
  const sanitizeMode = {
    onlyAlphabets,
    onlyNumeric,
    allowDecimal,
    allowSpecialChars,
    type,
  };

  // Empty values are owned by `required`. Whitespace-only is also empty when required
  // (RHF `required` treats "   " as filled). Caller `validation.validate` is merged
  // so it cannot wipe the whitespace / onlyAlphabets checks.
  const { validate: callerValidate, ...validationRest } = validation ?? {};

  const onlyAlphabetsLetters =
    onlyAlphabets && !validation?.pattern && !callerValidate
      ? {
          onlyAlphabetsLetters: (v: unknown) => {
            const s = v == null ? "" : String(v);
            if (s.trim() === "") return true;
            return (
              /^[a-zA-Z ]+$/.test(s) ||
              `${label || name} should only contain letters & spaces`
            );
          },
        }
      : undefined;

  const notOnlyWhitespace = required
    ? {
        notOnlyWhitespace: (v: unknown) => {
          if (v == null) return true;
          const s = String(v);
          if (s.length === 0) return true;
          return s.trim() !== "" || requiredMessage;
        },
      }
    : undefined;

  const mergedValidate = mergeRHFValidate(
    notOnlyWhitespace,
    onlyAlphabetsLetters,
    callerValidate as
      | ((value: unknown) => boolean | string)
      | Record<string, (value: unknown) => boolean | string>
      | undefined,
  );

  const {
    field: { onChange, value: fieldValue, onBlur, ...fieldRest },
    fieldState: { error, isTouched },
  } = useController({
    name,
    control,
    rules: {
      required: required ? requiredMessage : false,
      ...validationRest,
      ...(mergedValidate ? { validate: mergedValidate } : {}),
    },
    defaultValue: value,
  });

  // Validate on blur explicitly rather than relying on the parent form's
  // `mode` (most forms use the RHF default "onSubmit", which never
  // populates `fieldState.error` until a submit attempt — and Save stays
  // disabled while the form is invalid, so that submit can never happen).
  const handleBlur = () => {
    onBlur();
    trigger(name);
  };

  const errorMessage =
    ((isTouched || isSubmitted) && error) ||
    error?.type === "maxLength" ||
    error?.type === "max"
      ? (error?.message as string | undefined)
      : undefined;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let nextValue = e.target.value;

    // Clear any existing timeout immediately on new input
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Cover paste edge cases, autofill, and IME/input events that bypass keydown
    if (hasInputCharFilter(sanitizeMode)) {
      nextValue = sanitizeInputValue(nextValue, sanitizeMode);
    }

    if (uppercase) {
      nextValue = nextValue.toUpperCase();
    }

    // Check for maxLength in validation or rest
    const maxLengthRule = validation?.maxLength;
    const maxLength =
      maxLengthRule?.value || maxLengthRule || (rest as any).maxLength;

    if (maxLength && nextValue.length > maxLength) {
      // Manual trigger error
      const message =
        maxLengthRule?.message ||
        `Maximum length of ${maxLength} characters reached`;
      setError(name, { type: "maxLength", message });

      timeoutRef.current = setTimeout(() => {
        clearErrors(name);
      }, 1000);

      return; // Block input
    }

    // Check for max value limit if type is numeric
    if (isNumericMode && max !== undefined && nextValue !== "") {
      const numValue = parseFloat(nextValue);
      if (!isNaN(numValue) && numValue > Number(max)) {
        // Optional: Trigger error or just block
        setError(name, {
          type: "max",
          message: `Maximum value of ${max} exceeded`,
        });
        timeoutRef.current = setTimeout(() => {
          clearErrors(name);
        }, 1000);
        return; // Block input exceeding max
      }
    }

    // If typing valid input, clear specific errors
    if (error?.type === "maxLength" || error?.type === "max") {
      clearErrors(name);
    }

    // Sanitized string when filters stripped chars; otherwise pass the event
    if (nextValue !== e.target.value) {
      onChange(nextValue);
    } else {
      onChange(e);
    }

    // Must run unconditionally, not just "if (error)" -- react-hook-form's
    // formState.isValid lags a full render behind field.onChange under
    // mode: "onChange" (a known RHF quirk), so a Save button gated on
    // isValid stays disabled for the keystroke that actually made the form
    // valid, only catching up on the *next* interaction. Calling trigger()
    // here forces isValid to resolve in the same interaction.
    trigger(name);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    const allowedControlKeys = [
      "Backspace",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Home",
      "End",
    ];

    // Ctrl+C, Ctrl+V, Ctrl+A, Ctrl+Z, Cmd+ shortcuts etc.
    if (e.ctrlKey || e.metaKey) return;

    // Allow Control Keys
    if (allowedControlKeys.includes(key)) return;

    // ---------- ONLY ALPHABETS ----------
    if (onlyAlphabets) {
      const isAlphabet = SINGLE_ALPHABET_RE.test(key);
      const isSpace = key === " ";
      if (!isAlphabet && !isSpace) {
        e.preventDefault();
      }
      return;
    }

    // ---------- ONLY NUMERIC ----------
    if (isNumericMode) {
      const isNumber = SINGLE_DIGIT_RE.test(key);
      const isDot = key === ".";

      // Decimal handling
      if (isDot) {
        if (!allowDecimal) {
          e.preventDefault();
          return;
        }
        // Prevent multiple dots
        if ((e.target as HTMLInputElement).value.includes(".")) {
          e.preventDefault();
          return;
        }
      }

      if (!isNumber && !isDot) {
        e.preventDefault();
      }
      return;
    }

    // ---------- SPECIAL CHARACTERS BLOCK (default) ----------
    if (!onlyAlphabets && !isNumericMode && type !== "email") {
      const isSpecialChar = SPECIAL_CHAR_RE.test(key);

      if (isSpecialChar && !allowSpecialChars) {
        e.preventDefault();
      }
    }
  };

  /**
   * Paste must honor the same filters as keydown (Ctrl/Cmd+V bypasses keydown).
   * Only the clipboard chunk is sanitized/inserted — never re-filter the whole field
   * (would corrupt prefilled street addresses, hyphenated names, etc.).
   */
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!hasInputCharFilter(sanitizeMode)) return;

    const clipboard = e.clipboardData.getData("text");
    let sanitized = sanitizeInputValue(clipboard, sanitizeMode);
    if (uppercase) {
      sanitized = sanitized.toUpperCase();
    }

    if (sanitized === clipboard) return;

    e.preventDefault();
    if (!sanitized) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const input = e.currentTarget;
    const start = input.selectionStart ?? String(fieldValue ?? "").length;
    const end = input.selectionEnd ?? start;
    const current = String(fieldValue ?? "");
    const next = current.slice(0, start) + sanitized + current.slice(end);

    const maxLengthRule = validation?.maxLength;
    const maxLength =
      maxLengthRule?.value || maxLengthRule || (rest as any).maxLength;
    if (maxLength && next.length > maxLength) {
      const message =
        maxLengthRule?.message ||
        `Maximum length of ${maxLength} characters reached`;
      setError(name, { type: "maxLength", message });
      timeoutRef.current = setTimeout(() => {
        clearErrors(name);
      }, 1000);
      return;
    }

    if (isNumericMode && max !== undefined && next !== "") {
      const numValue = parseFloat(next);
      if (!isNaN(numValue) && numValue > Number(max)) {
        setError(name, {
          type: "max",
          message: `Maximum value of ${max} exceeded`,
        });
        timeoutRef.current = setTimeout(() => {
          clearErrors(name);
        }, 1000);
        return;
      }
    }

    if (error?.type === "maxLength" || error?.type === "max") {
      clearErrors(name);
    }

    onChange(next);
    trigger(name);
  };

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label className={TextCss.black.sub_heading_600_14}>
          {label}{" "}
          {isAsterisk && required && <span className="text-danger">*</span>}
        </label>
      )}
      <input
        {...fieldRest}
        {...rest}
        type={isNumericMode ? "text" : type}
        inputMode={isNumericMode ? "decimal" : undefined}
        value={fieldValue ?? ""}
        data-testid={`field-${name}`}
        className={`form-control ${errorMessage ? "is-invalid" : ""}`}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        max={max}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onChange={handleInputChange}
        onBlur={handleBlur}
      />
      {errorMessage && <div className="invalid-feedback">{errorMessage}</div>}
    </div>
  );
};

export default InputField;
